/* ==========================================================================
   VLB PDP — self-contained JS for the rebuilt product page.
   Vanilla custom elements following the theme's existing patterns. Relies
   only on globals the theme ships on every page: pubsub.js (publish/
   subscribe), constants.js (PUB_SUB_EVENTS), global.js (fetchConfig) and
   window.routes from layout/theme.liquid. Never touches old-PDP code.
   ========================================================================== */

/* Respect the OS motion preference for all JS-driven scrolling; read
   .matches at call time so mid-session preference changes are honored. */
const VLB_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* --- Add to Bag ----------------------------------------------------------
   Posts to /cart/add.js with the cart-drawer's sections so the existing
   global drawer re-renders and opens — the same contract product forms use
   across this theme. */

if (!customElements.get('vlb-product-form')) {
  customElements.define(
    'vlb-product-form',
    class VlbProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-vlb-atc]');
        this.errorBox = this.querySelector('[data-vlb-form-error]');
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const idInput = this.form.querySelector('[data-vlb-variant-input]');
        if (idInput && this.submitButton && !this.submitButton.hasAttribute('disabled')) {
          idInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton.hasAttribute('disabled')) return;

        // The sticky bar's button submits this form via its form= attribute;
        // show loading feedback on whichever button the user actually pressed.
        this.activeButton = evt.submitter || this.submitButton;

        this.toggleError(false);
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.activeButton.setAttribute('aria-disabled', 'true');
        this.activeButton.classList.add('loading');
        this.activeButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'vlb-product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.toggleError(true, response.description || response.message);
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.toggleError(true, window.cartStrings?.error);
          })
          .finally(() => {
            const button = this.activeButton || this.submitButton;
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            this.submitButton.removeAttribute('aria-disabled');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          });
      }

      toggleError(show, message) {
        if (!this.errorBox) return;
        this.errorBox.hidden = !show;
        if (message) this.errorBox.textContent = message;
        // Errors render in the buy box; if the user submitted from the sticky
        // bar the box is offscreen — bring it back (this also auto-hides the
        // sticky bar via its IntersectionObserver).
        if (show && this.errorBox.getBoundingClientRect().bottom < 0) {
          this.errorBox.scrollIntoView({
            block: 'center',
            behavior: VLB_REDUCED_MOTION.matches ? 'auto' : 'smooth',
          });
        }
      }
    }
  );
}

/* --- Variant orchestration -------------------------------------------------
   Re-render contract: option changes resolve the target variant from the
   embedded variants JSON, then re-fetch this section via the Section
   Rendering API (?variant=<id>&section_id=<id>) and swap the innerHTML of
   the whitelisted [data-vlb-swap] regions with server-rendered truth
   (price, installments, options/availability, back-in-stock, sticky info).
   Option inputs use delegated listeners so they survive their own swap.
   Publishes PUB_SUB_EVENTS.variantChange for the sticky bar and any other
   subscriber. */

if (!customElements.get('vlb-product-info')) {
  customElements.define(
    'vlb-product-info',
    class VlbProductInfo extends HTMLElement {
      static SWAP_REGIONS = ['price', 'installments', 'options', 'bis', 'sticky-info'];

      constructor() {
        super();
        this.abortController = undefined;
        try {
          this.variants = JSON.parse(this.querySelector('[data-vlb-variants]').textContent);
        } catch (e) {
          this.variants = [];
        }
        this.addEventListener('change', (e) => {
          if (e.target.matches('[data-vlb-option-input]')) this.onOptionChange(e.target.id);
        });
        this.addEventListener('click', (e) => {
          const opener = e.target.closest('[data-vlb-open-accordion]');
          if (opener) {
            const target = document.querySelector(opener.getAttribute('href'));
            if (target) {
              e.preventDefault();
              if (target.tagName === 'DETAILS') target.open = true;
              target.scrollIntoView({
                behavior: VLB_REDUCED_MOTION.matches ? 'auto' : 'smooth',
                block: 'start',
              });
            }
          }
        });
      }

      selectedOptions() {
        return Array.from(this.querySelectorAll('[data-vlb-option-fieldset]')).map(
          (fieldset) => fieldset.querySelector('[data-vlb-option-input]:checked')?.value
        );
      }

      resolveVariant(selected) {
        return this.variants.find((variant) =>
          selected.every((value, i) => (variant[`option${i + 1}`] || undefined) === value)
        );
      }

      onOptionChange(targetId) {
        const variant = this.resolveVariant(this.selectedOptions());
        if (!variant) {
          this.setAtcState(false, window.variantStrings?.unavailable || 'Unavailable');
          return;
        }
        this.renderVariant(variant, targetId);
      }

      setAtcState(available, text) {
        this.querySelectorAll('[data-vlb-atc]').forEach((button) => {
          button.toggleAttribute('disabled', !available);
          const label = button.querySelector('[data-vlb-atc-text]');
          if (label && text) label.textContent = text;
        });
        this.querySelectorAll('[data-vlb-variant-input]').forEach((input) => {
          input.disabled = !available;
        });
      }

      async renderVariant(variant, targetId) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.classList.add('vlb-updating');

        const url = `${this.dataset.productUrl}?variant=${variant.id}&section_id=${this.dataset.section}`;
        try {
          const response = await fetch(url, { signal: controller.signal });
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');

          VlbProductInfo.SWAP_REGIONS.forEach((region) => {
            const source = doc.querySelector(`[data-vlb-swap="${region}"]`);
            const destination = this.querySelector(`[data-vlb-swap="${region}"]`);
            if (source && destination) destination.innerHTML = source.innerHTML;
          });

          // The options swap re-renders identical deterministic input ids —
          // restore keyboard focus to the radio the user just activated.
          if (targetId) this.querySelector(`#${CSS.escape(targetId)}`)?.focus();

          // Form state: variant id + ATC availability, mirrored from the
          // server-rendered button so copy stays Liquid-owned.
          this.querySelectorAll('[data-vlb-variant-input]').forEach((input) => {
            input.value = variant.id;
            input.disabled = !variant.available;
          });
          const freshAtc = doc.querySelector('[data-vlb-atc]');
          this.setAtcState(
            variant.available,
            freshAtc?.querySelector('[data-vlb-atc-text]')?.textContent.trim()
          );

          // Gallery: show this variant's image first.
          const mediaId = variant.featured_media?.id || variant.featured_image?.id;
          if (mediaId) {
            this.querySelector('vlb-gallery')?.setActiveMedia(mediaId, true);
          }

          window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
          this.dataset.variantId = String(variant.id);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.dataset.section, variant: variant },
            });
          }
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          // Only the request that still owns the controller clears the
          // loading state — an aborted call must not strip it from the
          // superseding in-flight request.
          if (this.abortController === controller) this.classList.remove('vlb-updating');
        }
      }
    }
  );
}

/* --- Sticky mobile Add to Bag ----------------------------------------------
   Shows the bar once the buy box has scrolled above the viewport; hides it
   again when the buy box is visible (or the user is above it). Fixed
   overlay — never shifts layout. */

if (!customElements.get('vlb-sticky-atc')) {
  customElements.define(
    'vlb-sticky-atc',
    class VlbStickyAtc extends HTMLElement {
      connectedCallback() {
        const buyBox = this.closest('vlb-product-info')?.querySelector('.vlb-buy');
        if (!buyBox || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            // records arrive oldest-first — the last one is current state
            const entry = entries[entries.length - 1];
            const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.classList.toggle('is-visible', passed);
          },
          { threshold: 0 }
        );
        this.observer.observe(buyBox);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );
}

/* --- Gallery --------------------------------------------------------------
   Scroll-snap track + thumbnail sync, exposes setActiveMedia(mediaId) for
   variant changes, and a zoom lightbox with double-tap and two-finger pinch. */

if (!customElements.get('vlb-gallery')) {
  customElements.define(
    'vlb-gallery',
    class VlbGallery extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('[data-vlb-track]');
        this.slides = Array.from(this.querySelectorAll('[data-vlb-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-vlb-thumb]'));
        this.thumbsRow = this.querySelector('[data-vlb-thumbs]');
        this.lightbox = this.querySelector('[data-vlb-lightbox]');
        this.stage = this.querySelector('[data-vlb-lightbox-stage]');

        // Teardown on the dialog's native close event so EVERY close path
        // (close button, Escape/cancel, in-app browser back gesture) clears
        // the scroll lock and stops any playing lightbox video.
        this.lightbox?.addEventListener('close', () => {
          if (this.stage) this.stage.innerHTML = '';
          document.documentElement.style.overflow = '';
        });

        if (this.track) {
          this.track.addEventListener('scroll', () => {
            window.requestAnimationFrame(() => this.syncActiveFromScroll());
          });
        }
        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.mediaId, true));
        });
        this.querySelectorAll('[data-vlb-zoom]').forEach((btn) => {
          btn.addEventListener('click', () => this.openLightbox(btn.dataset.mediaId));
        });
        this.querySelector('[data-vlb-lightbox-close]')?.addEventListener('click', () =>
          this.closeLightbox()
        );
        // The stage fills the dialog, so "backdrop" taps land on the stage
        // itself — close when the tap missed the media.
        this.stage?.addEventListener('click', (e) => {
          if (e.target === this.stage) this.closeLightbox();
        });
        this.initPinch();
      }

      slideIndexFor(mediaId) {
        return this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      }

      syncActiveFromScroll() {
        if (!this.slides.length) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        const slide = this.slides[Math.min(index, this.slides.length - 1)];
        if (slide) this.markActive(slide.dataset.mediaId);
      }

      markActive(mediaId) {
        this.thumbs.forEach((t) => {
          const active = t.dataset.mediaId === String(mediaId);
          t.classList.toggle('is-active', active);
          if (active) {
            t.setAttribute('aria-current', 'true');
            // Scroll only the thumb strip — never scrollIntoView, which
            // would also scroll the page and yank the viewport away from
            // the swatches the user is interacting with.
            if (this.thumbsRow) {
              this.thumbsRow.scrollTo({
                left:
                  t.offsetLeft -
                  this.thumbsRow.offsetLeft -
                  (this.thumbsRow.clientWidth - t.offsetWidth) / 2,
                behavior: VLB_REDUCED_MOTION.matches ? 'auto' : 'smooth',
              });
            }
          } else {
            t.removeAttribute('aria-current');
          }
        });
      }

      setActiveMedia(mediaId, smooth) {
        const index = this.slideIndexFor(mediaId);
        if (index < 0) return;
        this.track.scrollTo({
          left: index * this.track.clientWidth,
          behavior: smooth && !VLB_REDUCED_MOTION.matches ? 'smooth' : 'auto',
        });
        this.markActive(mediaId);
      }

      openLightbox(mediaId) {
        const template = this.querySelector(`[data-vlb-lightbox-media="${mediaId}"]`);
        if (!template || !this.lightbox) return;
        this.stage.innerHTML = '';
        this.stage.appendChild(template.content.cloneNode(true));
        this.resetZoom();
        this.lightbox.showModal();
        document.documentElement.style.overflow = 'hidden';
      }

      closeLightbox() {
        // Teardown happens in the dialog's 'close' event handler, so every
        // close path shares the same cleanup.
        if (this.lightbox?.open) this.lightbox.close();
      }

      /* Pinch / double-tap zoom inside the lightbox stage. */
      initPinch() {
        if (!this.stage) return;
        this.zoom = { scale: 1, x: 0, y: 0 };
        const pointers = new Map();
        let startDist = 0;
        let startScale = 1;
        let lastTap = 0;
        let panStart = null;

        const apply = () => {
          const img = this.stage.querySelector('img');
          if (!img) return;
          img.style.transform = `translate(${this.zoom.x}px, ${this.zoom.y}px) scale(${this.zoom.scale})`;
        };
        this.resetZoom = () => {
          this.zoom = { scale: 1, x: 0, y: 0 };
          apply();
        };

        this.stage.addEventListener('pointerdown', (e) => {
          if (!this.stage.querySelector('img')) return;
          pointers.set(e.pointerId, e);
          if (pointers.size === 2) {
            const [a, b] = Array.from(pointers.values());
            startDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            startScale = this.zoom.scale;
          } else if (pointers.size === 1) {
            const now = Date.now();
            if (now - lastTap < 300) {
              this.zoom.scale = this.zoom.scale > 1 ? 1 : 2.5;
              if (this.zoom.scale === 1) {
                this.zoom.x = 0;
                this.zoom.y = 0;
              }
              apply();
            }
            lastTap = now;
            panStart = { x: e.clientX - this.zoom.x, y: e.clientY - this.zoom.y };
          }
        });

        this.stage.addEventListener('pointermove', (e) => {
          if (!pointers.has(e.pointerId)) return;
          pointers.set(e.pointerId, e);
          if (pointers.size === 2) {
            const [a, b] = Array.from(pointers.values());
            const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            this.zoom.scale = Math.min(5, Math.max(1, (startScale * dist) / startDist));
            apply();
          } else if (pointers.size === 1 && this.zoom.scale > 1 && panStart) {
            this.zoom.x = e.clientX - panStart.x;
            this.zoom.y = e.clientY - panStart.y;
            apply();
          }
        });

        const release = (e) => {
          pointers.delete(e.pointerId);
          if (pointers.size < 2) startDist = 0;
          if (pointers.size === 0) panStart = null;
          if (this.zoom.scale <= 1.02) {
            this.zoom = { scale: 1, x: 0, y: 0 };
            apply();
          }
        };
        this.stage.addEventListener('pointerup', release);
        this.stage.addEventListener('pointercancel', release);
      }
    }
  );
}
